import React, { useEffect, useState, useRef, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, CreditCard, ChevronDown, GraduationCap, CalendarCheck, Menu, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
export function Header() {
  const {
    language,
    toggleLanguage,
    t
  } = useLanguage();
  const [payOpen, setPayOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setPayOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Close mobile menu when clicking a link
  const handleMobileLinkClick = () => {
    setMobileMenuOpen(false);
  };
  const whatsappUrl = 'https://wa.me/13472648498?text=' + encodeURIComponent(language === 'es' ? 'Hola Lorena, me gustaría agendar una cita.' : 'Hi Lorena, I would like to book an appointment.');
  return <header className="sticky top-0 z-50 w-full bg-white/95 backdrop-blur-sm border-b border-gold/20 shadow-sm" data-id="element-168">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-169">
        <div className="flex justify-between items-center h-16 md:h-20" data-id="element-170">
          <div className="flex-shrink-0 flex items-center" data-id="element-171">
            <a href="#" data-id="element-172">
              <img src="https://cdn.magicpatterns.com/uploads/f2nJaQykRwe1vHYLG5oJtG/lore_logo.png" alt="Lorena Beauty House Logo" className="h-12 md:h-16 w-auto object-contain mix-blend-multiply" data-id="element-173" />
            </a>
          </div>

          {/* Action Buttons & Hamburger (Visible on all screens) */}
          <div className="flex items-center space-x-2 md:space-x-4" data-id="element-174">
            {/* Language Toggle */}
            <button onClick={toggleLanguage} className="flex items-center space-x-0.5 md:space-x-1 text-[10px] md:text-sm font-bold border border-gold/30 rounded-full px-1.5 md:px-3 py-0.5 md:py-1 hover:bg-gold/10 transition-colors" data-id="element-175">
              <span className={language === 'es' ? 'text-gold-dark' : 'text-dark/50'} data-id="element-176">
                ES
              </span>
              <span className="text-dark/30" data-id="element-177">|</span>
              <span className={language === 'en' ? 'text-gold-dark' : 'text-dark/50'} data-id="element-178">
                EN
              </span>
            </button>

            {/* WhatsApp Agendar Button */}
            <motion.a whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center bg-gold hover:bg-gold-dark text-white w-8 h-8 md:w-auto md:h-auto md:px-4 md:py-2 rounded-sm text-sm font-medium transition-colors shadow-md" aria-label={t('header', 'book')} data-id="element-179">
              <MessageCircle className="w-4 h-4 md:w-4 md:h-4 md:mr-2" data-id="element-180" />
              <span className="hidden md:inline" data-id="element-181">{t('header', 'book')}</span>
            </motion.a>

            {/* Pagar Dropdown */}
            <div className="relative" ref={dropdownRef} data-id="element-182">
              <motion.button whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} onClick={() => setPayOpen(!payOpen)} className="flex items-center justify-center border border-gold text-gold hover:bg-gold hover:text-white w-8 h-8 md:w-auto md:h-auto md:px-3 md:py-2 rounded-sm text-sm font-medium transition-all" aria-label={language === 'es' ? 'Pagar' : 'Pay'} data-id="element-183">
                <CreditCard className="w-4 h-4 md:w-4 md:h-4 md:mr-2" data-id="element-184" />
                <span className="hidden md:inline" data-id="element-185">
                  {language === 'es' ? 'Pagar' : 'Pay'}
                </span>
                <ChevronDown className={`hidden md:block w-3 h-3 ml-1 transition-transform duration-300 ${payOpen ? 'rotate-180' : ''}`} data-id="element-186" />
              </motion.button>
              <AnimatePresence data-id="element-187">
                {payOpen && <motion.div initial={{
                opacity: 0,
                y: -8
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -8
              }} transition={{
                duration: 0.2
              }} className="absolute right-0 mt-2 w-56 md:w-64 bg-white border border-gold/20 rounded-sm shadow-xl overflow-hidden z-50" data-id="element-188">
                    <a href="https://buy.stripe.com/bJedR855raja78m6493cc02" target="_blank" rel="noopener noreferrer" className="flex items-center space-x-3 px-4 py-3 md:px-5 md:py-4 hover:bg-beige transition-colors border-b border-gold/10" onClick={() => setPayOpen(false)} data-id="element-189">
                      <CalendarCheck className="w-4 h-4 md:w-5 md:h-5 text-gold flex-shrink-0" data-id="element-190" />
                      <div data-id="element-191">
                        <p className="font-bold text-dark text-xs md:text-sm" data-id="element-192">
                          {language === 'es' ? 'Pagar Cita' : 'Pay Appointment'}
                        </p>
                        <p className="hidden md:block text-xs text-dark/50" data-id="element-193">
                          {language === 'es' ? 'Pago seguro con Stripe' : 'Secure payment via Stripe'}
                        </p>
                      </div>
                    </a>
                    <a href="https://book.stripe.com/14A3cucxT2QIgIWdwB3cc01" target="_blank" rel="noopener noreferrer" className="flex items-center space-x-3 px-4 py-3 md:px-5 md:py-4 hover:bg-beige transition-colors" onClick={() => setPayOpen(false)} data-id="element-194">
                      <GraduationCap className="w-4 h-4 md:w-5 md:h-5 text-gold flex-shrink-0" data-id="element-195" />
                      <div data-id="element-196">
                        <p className="font-bold text-dark text-xs md:text-sm" data-id="element-197">
                          {language === 'es' ? 'Inscripción Master Class' : 'Master Class Enrollment'}
                        </p>
                        <p className="hidden md:block text-xs text-dark/50" data-id="element-198">
                          {language === 'es' ? 'Reserva tu lugar' : 'Reserve your spot'}
                        </p>
                      </div>
                    </a>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* Hamburger Menu Button */}
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="flex items-center justify-center w-8 h-8 md:w-10 md:h-10 border border-gold/40 rounded-sm text-gold hover:bg-gold hover:text-white transition-colors flex-shrink-0" aria-label="Toggle menu" data-id="element-199">
              {mobileMenuOpen ? <X className="w-5 h-5 md:w-6 md:h-6" data-id="element-200" /> : <Menu className="w-5 h-5 md:w-6 md:h-6" data-id="element-201" />}
            </button>
          </div>
        </div>
      </div>

      {/* Elegant Menu Overlay (Visible on all screens) */}
      <AnimatePresence data-id="element-202">
        {mobileMenuOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: '100vh'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} className="fixed inset-x-0 top-16 md:top-20 bg-white/95 backdrop-blur-md z-40 overflow-y-auto border-t border-gold/10" data-id="element-203">
            <nav className="flex flex-col items-center py-10 md:py-16 space-y-6 md:space-y-8" data-id="element-204">
              <a href="#servicios" onClick={handleMobileLinkClick} className="text-2xl md:text-4xl font-serif text-dark hover:text-gold transition-colors" data-id="element-205">
                {t('header', 'services')}
              </a>
              <a href="#sobre-lorena" onClick={handleMobileLinkClick} className="text-2xl md:text-4xl font-serif text-dark hover:text-gold transition-colors" data-id="element-206">
                {t('header', 'about')}
              </a>
              <a href="#testimonios" onClick={handleMobileLinkClick} className="text-2xl md:text-4xl font-serif text-dark hover:text-gold transition-colors" data-id="element-207">
                {t('header', 'testimonials')}
              </a>
              <a href="#productos" onClick={handleMobileLinkClick} className="text-2xl md:text-4xl font-serif text-dark hover:text-gold transition-colors text-center px-4" data-id="element-208">
                {t('header', 'products')}
              </a>
              <a href="#espacio" onClick={handleMobileLinkClick} className="text-2xl md:text-4xl font-serif text-dark hover:text-gold transition-colors" data-id="element-209">
                {t('header', 'space')}
              </a>
              <a href="#formacion" onClick={handleMobileLinkClick} className="text-2xl md:text-4xl font-serif text-dark hover:text-gold transition-colors text-center px-4" data-id="element-210">
                {t('header', 'training')}
              </a>
              <a href="#contacto" onClick={handleMobileLinkClick} className="text-2xl md:text-4xl font-serif text-dark hover:text-gold transition-colors" data-id="element-211">
                {t('header', 'contact')}
              </a>

              <div className="w-12 md:w-24 h-0.5 bg-gold/30 mt-6 md:mt-8" data-id="element-212"></div>

              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 bg-gold text-white px-8 py-3 md:px-10 md:py-4 rounded-sm text-lg md:text-xl font-medium shadow-md mt-6 md:mt-8 hover:bg-gold-dark transition-colors" data-id="element-213">
                <MessageCircle className="w-5 h-5 md:w-6 md:h-6" data-id="element-214" />
                <span data-id="element-215">{t('header', 'book')}</span>
              </a>
            </nav>
          </motion.div>}
      </AnimatePresence>
    </header>;
}